import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  EditorHero,
  MonoLabel,
  Monogram,
  SectionCap,
  Pill,
} from '../components/Editorial';

const PROFILE_TABS = [
  { label: 'Info', to: '/profile' },
  { label: 'Academics', to: '/academics' },
  { label: 'Photos', to: '/photos' },
  { label: 'Videos', to: '/videos' },
  { label: 'Social', to: '/social' },
  { label: 'My Sports', to: '/my-sports' },
  { label: 'Stats', to: '/stats' },
  { label: 'Measurables', to: '/measurables' },
  { label: 'Preferences', to: '/preferences', active: true },
];

const HOBBIES = [
  { value: 'gaming', label: 'Gaming' },
  { value: 'music', label: 'Music' },
  { value: 'social_media', label: 'Social media' },
  { value: 'fitness_gym', label: 'Fitness' },
  { value: 'reading_audiobooks', label: 'Reading' },
  { value: 'watching_sports', label: 'Watching sports' },
  { value: 'photography_video', label: 'Photography' },
  { value: 'cooking_food', label: 'Cooking' },
  { value: 'outdoor_activities', label: 'Outdoor activities' },
  { value: 'collecting_building', label: 'Collecting' },
  { value: 'faith', label: 'Faith' },
  { value: 'volunteering', label: 'Volunteering' },
  { value: 'art', label: 'Art' },
  { value: 'travel', label: 'Travel' },
  { value: 'dance', label: 'Dance' },
  { value: 'coding', label: 'Coding' },
];

const MAJORS = [
  { value: 'biology', label: 'Biology' },
  { value: 'business', label: 'Business' },
  { value: 'communications', label: 'Communications' },
  { value: 'computer_science', label: 'Computer Science' },
  { value: 'education', label: 'Education' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'health', label: 'Health' },
  { value: 'psychology', label: 'Psychology' },
  { value: 'social_sciences', label: 'Social Sciences' },
  { value: 'visual_performing_arts', label: 'Visual & Performing Arts' },
];

const REGIONS = [
  { value: 'west', label: 'West' },
  { value: 'midwest', label: 'Midwest' },
  { value: 'northeast', label: 'Northeast' },
  { value: 'southwest', label: 'Southwest' },
  { value: 'southeast', label: 'Southeast' },
];

const DIVISIONS = [
  { value: 'd1', label: 'D1' },
  { value: 'd2', label: 'D2' },
  { value: 'd3', label: 'D3' },
  { value: 'naia', label: 'NAIA' },
  { value: 'juco', label: 'JUCO' },
];

const SCHOOL_SIZES = [
  { value: 'small', label: 'Small (<5k)' },
  { value: 'medium', label: 'Medium (5–15k)' },
  { value: 'large', label: 'Large (15k+)' },
];

const CAMPUS_SETTINGS = [
  { value: 'urban', label: 'Urban' },
  { value: 'suburban', label: 'Suburban' },
  { value: 'rural', label: 'Rural' },
];

const SCHOOL_TYPES = [
  { value: 'any', label: 'No preference' },
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
];

export default function PreferencesPage() {
  const { user, API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [profile, setProfile] = useState({
    hobbies: [],
    major_preferences: [],
    majors_other: '',
    college_region_preferences: [],
    division_preferences: [],
    campus_setting_preferences: [],
    school_size_preference: '',
    school_type_preference: 'any',
    high_school: '',
    city: '',
    state: '',
    graduation_year: '',
    sports_played: [],
    position_primary: '',
    position_secondary: '',
    player_photo: '',
  });

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const r = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (r.data) {
        setProfile(prev => ({
          ...prev,
          ...r.data,
          hobbies: r.data.hobbies || [],
          major_preferences: r.data.major_preferences || [],
          majors_other: r.data.majors_other || '',
          college_region_preferences: r.data.college_region_preferences || [],
          division_preferences: r.data.division_preferences || [],
          campus_setting_preferences: r.data.campus_setting_preferences || [],
          school_size_preference: r.data.school_size_preference || '',
          school_type_preference: r.data.school_type_preference || 'any',
        }));
      }
    } catch (e) { /* no profile yet */ }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 2200);
  };

  const saveField = async (name, value) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, { [name]: value }, getAxiosConfig());
      showMessage('Saved');
    } catch (e) {
      showMessage(e.response?.data?.detail || 'Error saving', 'error');
    }
  };

  const toggleMulti = (name, value) => {
    const cur = profile[name] || [];
    const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
    setProfile(prev => ({ ...prev, [name]: next }));
    saveField(name, next);
  };

  const setSingle = (name, value, allowDeselect = true) => {
    const cur = profile[name];
    const next = (allowDeselect && cur === value) ? '' : value;
    setProfile(prev => ({ ...prev, [name]: next }));
    saveField(name, next);
  };

  /* Hero helpers */
  const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');
  const sport = (profile.sports_played || []).join(', ');
  const positions = [profile.position_primary, profile.position_secondary].filter(Boolean).join(' / ');
  const sportLine = [sport, positions].filter(Boolean).join(' · ');
  const subParts = [];
  if (profile.graduation_year) subParts.push(`Class of '${profile.graduation_year.toString().slice(-2)}`);
  if (profile.high_school) subParts.push([profile.high_school, [profile.city, profile.state].filter(Boolean).join(', ')].filter(Boolean).join(' — '));
  if (sportLine) subParts.push(sportLine);

  const renderMultiPills = (name, options) => (
    <div className="ed-card">
      <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {options.map(opt => {
          const on = (profile[name] || []).includes(opt.value);
          return (
            <Pill
              key={opt.value}
              variant={on ? 'active' : 'ghost'}
              onClick={() => toggleMulti(name, opt.value)}
              style={{ cursor: 'pointer' }}
            >
              {opt.label}
            </Pill>
          );
        })}
      </div>
    </div>
  );

  const renderSinglePills = (name, options, allowDeselect = true) => (
    <div className="ed-card">
      <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {options.map(opt => {
          const on = profile[name] === opt.value;
          return (
            <Pill
              key={opt.value}
              variant={on ? 'active' : 'ghost'}
              onClick={() => setSingle(name, opt.value, allowDeselect)}
              style={{ cursor: 'pointer' }}
            >
              {opt.label}
            </Pill>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="ed-page">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>PROFILE · PREFERENCES</MonoLabel>
            {profile.high_school && <MonoLabel onDark>·</MonoLabel>}
            {profile.high_school && <MonoLabel onDark>{profile.high_school.toUpperCase()}</MonoLabel>}
          </>
        }
        prefix={<Monogram initials={initials} photoUrl={profile.player_photo || null} />}
        headline="Beyond the"
        accent="field."
        sub="Show coaches the human side. Pick the hobbies that fit you and the schools you'd consider playing for."
      />

      {/* Tab bar */}
      <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {PROFILE_TABS.map(tab => (
          tab.active ? (
            <Pill key={tab.label} variant="active">{tab.label}</Pill>
          ) : (
            <Link key={tab.label} to={tab.to} style={{ textDecoration: 'none' }}>
              <Pill variant="ghost">{tab.label}</Pill>
            </Link>
          )
        ))}
      </div>

      <SectionCap label="Hobbies" hint="MULTI-SELECT" />
      {renderMultiPills('hobbies', HOBBIES)}

      <SectionCap label="College Majors" hint="MULTI-SELECT" />
      {renderMultiPills('major_preferences', MAJORS)}
      <div className="ed-card">
        <div className="form-field" style={{ marginBottom: 0 }}>
          <label className="form-field__label" htmlFor="majors_other">Other majors</label>
          <input
            id="majors_other"
            type="text"
            maxLength={500}
            value={profile.majors_other || ''}
            onChange={(e) => setProfile(prev => ({ ...prev, majors_other: e.target.value }))}
            onBlur={() => saveField('majors_other', profile.majors_other || '')}
            className="form-field__input"
            placeholder="Anything not listed above..."
          />
        </div>
      </div>

      <SectionCap label="College Region" hint="MULTI-SELECT" />
      {renderMultiPills('college_region_preferences', REGIONS)}

      <SectionCap label="Division" hint="MULTI-SELECT" />
      {renderMultiPills('division_preferences', DIVISIONS)}

      <SectionCap label="School Size" hint="ONE — TAP AGAIN TO CLEAR" />
      {renderSinglePills('school_size_preference', SCHOOL_SIZES, true)}

      <SectionCap label="Campus Setting" hint="MULTI-SELECT" />
      {renderMultiPills('campus_setting_preferences', CAMPUS_SETTINGS)}

      <SectionCap label="Public or Private" hint="ONE" />
      {renderSinglePills('school_type_preference', SCHOOL_TYPES, false)}
    </div>
  );
}
